<template>
  <v-container
    fluid
    class="br-page"
    :class="{ 'br-page--dark': $vuetify.theme.dark }"
  >
    <section>
      <!-- Top Sales Center Navigation / Tab Switcher -->
      <v-card class="br-sales-nav mb-3" flat>
        <v-card-text class="d-flex align-center flex-wrap py-2 px-3">
          <div class="d-flex align-center mr-4">
            <span class="br-accent-bar mr-2" />
            <h1 class="br-page-title mb-0">{{ pageTitle }}</h1>
          </div>

          <v-spacer />

          <div class="br-tabs">
            <div
              class="br-tab"
              :class="{ 'br-tab--active': activeTab === 'bills' }"
              @click="switchTab('bills')"
            >
              <v-icon
                size="13"
                class="mr-1"
                :color="activeTab === 'bills' ? 'primary' : 'grey'"
              >
                fal fa-receipt
              </v-icon>
              {{ $t("report_bill") || "ໃບບິນ" }}
            </div>
            <div
              class="br-tab"
              :class="{ 'br-tab--active': activeTab === 'product' }"
              @click="switchTab('product')"
            >
              <v-icon
                size="13"
                class="mr-1"
                :color="activeTab === 'product' ? 'primary' : 'grey'"
              >
                fal fa-boxes
              </v-icon>
              {{ $t("by_product") || "ຕາມສິນຄ້າ" }}
            </div>
            <div
              class="br-tab"
              :class="{ 'br-tab--active': activeTab === 'day' }"
              @click="switchTab('day')"
            >
              <v-icon
                size="13"
                class="mr-1"
                :color="activeTab === 'day' ? 'primary' : 'grey'"
              >
                fal fa-calendar-day
              </v-icon>
              {{ $t("by_day") || "ຕາມມື້" }}
            </div>
            <div
              class="br-tab"
              :class="{ 'br-tab--active': activeTab === 'cancelled_items' }"
              @click="switchTab('cancelled_items')"
            >
              <v-icon
                size="13"
                class="mr-1"
                :color="activeTab === 'cancelled_items' ? 'warning' : 'grey'"
              >
                fal fa-ban
              </v-icon>
              {{ $t("cancelled_items") || "ຍົກເລີກລາຍການ" }}
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Tab Views with keep-alive -->
      <keep-alive>
        <tab-bills
          v-if="activeTab === 'bills'"
          key="bills"
          :start-date="sharedStartDate"
          :end-date="sharedEndDate"
          @date-change="onDateChange"
        />
        <tab-bills
          v-else-if="activeTab === 'cancelled_items'"
          key="cancelled_items"
          :initial-status-tab="'cancelled_items'"
          :start-date="sharedStartDate"
          :end-date="sharedEndDate"
          @date-change="onDateChange"
        />
        <tab-products
          v-else
          key="products"
          :initial-view="activeTab === 'day' ? 'day' : 'product'"
          :start-date="sharedStartDate"
          :end-date="sharedEndDate"
          @date-change="onDateChange"
          @view-change="onProductViewChange"
        />
      </keep-alive>
    </section>
  </v-container>
</template>

<script>
import TabBills from "@/pages/reports/sales/TabBills.vue";
import TabProducts from "@/pages/reports/sales/TabProducts.vue";

export default {
  name: "SalesReport",
  components: {
    TabBills,
    TabProducts,
  },
  data() {
    return {
      activeTab: "bills",
      sharedStartDate: "",
      sharedEndDate: "",
    };
  },
  computed: {
    pageTitle() {
      if (this.activeTab === "bills") {
        return this.$t("report_bill") || "ໃບບິນ";
      }
      if (this.activeTab === "cancelled_items") {
        return this.$t("cancelled_items") || "ລາຍການທີ່ຍົກເລີກ";
      }
      return this.$t("product_sales_report") || "ລາຍງານການຂາຍ";
    },
  },
  watch: {
    "$route.query.tab": {
      immediate: true,
      handler(tab) {
        if (tab) {
          const normalized = tab === "products" ? "product" : tab;
          if (["bills", "product", "day", "cancelled_items"].includes(normalized)) {
            this.activeTab = normalized;
          }
        }
      },
    },
  },
  methods: {
    switchTab(tab) {
      if (this.activeTab === tab) return;
      this.activeTab = tab;
      if (this.$route.query.tab !== tab) {
        this.$router
          .replace({
            query: {
              ...this.$route.query,
              tab: tab === "bills" ? undefined : tab,
            },
          })
          .catch(() => {});
      }
    },
    onDateChange({ startDate, endDate }) {
      this.sharedStartDate = startDate;
      this.sharedEndDate = endDate;
    },
    onProductViewChange(view) {
      if (view === "day" && this.activeTab !== "day") {
        this.activeTab = "day";
        this.syncRouteTab("day");
      } else if (view === "product" && this.activeTab !== "product") {
        this.activeTab = "product";
        this.syncRouteTab("product");
      }
    },
    syncRouteTab(tab) {
      if (this.$route.query.tab !== tab) {
        this.$router
          .replace({
            query: {
              ...this.$route.query,
              tab,
            },
          })
          .catch(() => {});
      }
    },
  },
};
</script>

<style scoped>
.br-page {
  --br-accent: var(--v-primary-base, #5b39c9);
  --br-border: #e2e8f0;
  --br-muted: #94a3b8;
  --br-text: #141021;
  --br-radius-lg: 16px;
  --br-radius-md: 10px;
  max-width: 100%;
  background: #f6f6fa;
}

.br-page--dark {
  --br-text: #ffffff;
  background: #1b1d30;
}

.br-page section {
  width: 100%;
}

.br-sales-nav {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
}

.br-page--dark .br-sales-nav {
  background: #232742 !important;
  border-color: #313759 !important;
}

.br-accent-bar {
  display: inline-block;
  width: 4px;
  height: 20px;
  border-radius: 2px;
  background: var(--br-accent);
}

.br-page-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--br-text);
  letter-spacing: -0.01em;
}

/* Tabs */
.br-tabs {
  display: inline-flex;
  height: 38px;
  box-sizing: border-box;
  align-items: center;
  gap: 4px;
  background: #f1f2f7;
  border: 1px solid #e2e8f0;
  padding: 3px;
  border-radius: 10px;
}

.br-page--dark .br-tabs {
  background: #1b1d30;
  border-color: rgba(154, 140, 240, 0.16);
}

.br-tab {
  height: 30px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  padding: 4px 12px;
  border-radius: 7px;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.br-tab:hover {
  color: #141021;
}

.br-page--dark .br-tab:hover {
  color: #e2e8f0;
}

.br-tab--active {
  background: #ffffff !important;
  color: #5b39c9 !important;
  font-weight: 700;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.br-page--dark .br-tab--active {
  background: rgba(154, 140, 240, 0.18) !important;
  color: #c4b5fd !important;
  box-shadow: none;
}
</style>
